/**
 * Turning the worker's raw pixels into something a page can show.
 *
 * The worker sends `{data, width, height, channels}` rather than an encoded
 * image, for two reasons. It has no DOM, and `OffscreenCanvas` is not something
 * to depend on across every browser the bay is meant to run in. And a raw
 * buffer is TRANSFERABLE, so a 384x384 picture crosses the thread boundary
 * without being copied.
 *
 * Encoding belongs here, on the side that actually has a canvas.
 */

import type { ImageResult } from "./protocol";

/**
 * Widen RGB to RGBA, because `ImageData` only accepts four channels.
 *
 * Janus decodes to three. Handing `putImageData` a 3-channel buffer does not
 * throw, it reads past the end of each row and produces a sheared, colour-
 * shifted mess that looks like a model failure rather than a plumbing one.
 */
export function toRGBA(img: ImageResult): Uint8ClampedArray {
  const { data, width, height, channels } = img;
  const out = new Uint8ClampedArray(width * height * 4);
  if (channels === 4) {
    out.set(data);
    return out;
  }
  if (channels !== 3) {
    throw new Error(`unexpected channel count ${channels}; expected 3 or 4`);
  }
  for (let i = 0, j = 0; i < data.length; i += 3, j += 4) {
    out[j] = data[i];
    out[j + 1] = data[i + 1];
    out[j + 2] = data[i + 2];
    out[j + 3] = 255;
  }
  return out;
}

/** Paint one result onto a fresh canvas. */
export function toCanvas(img: ImageResult): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("could not get a 2d context to render the image");
  ctx.putImageData(new ImageData(toRGBA(img), img.width, img.height), 0, 0);
  return canvas;
}

/** A PNG data URL, suitable for an <img src> or a download link. */
export function toDataURL(img: ImageResult): string {
  return toCanvas(img).toDataURL("image/png");
}

/** A PNG blob, for saving without the base64 round trip. */
export function toBlob(img: ImageResult): Promise<Blob> {
  return new Promise((resolve, reject) => {
    toCanvas(img).toBlob(
      (b) => (b ? resolve(b) : reject(new Error("canvas produced no blob"))),
      "image/png",
    );
  });
}
